import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { guidFor } from '@ember/object/internals';
import { TrackedAsyncData } from 'ember-async-data';
import YupValidations from '../../classes/yup-validations';
import { number } from 'yup';
import { task } from 'ember-concurrency';
import { DateTime } from 'luxon';
import t from 'ember-intl/helpers/t';
import ToggleYesno from '../toggle-yesno';
import perform from 'ember-concurrency/helpers/perform';
import EditableField from '../editable-field';
import { on } from '@ember/modifier';
import pick from '../../helpers/pick';
import set from 'ember-set-helper/helpers/set';
import YupValidationMessage from '../yup-validation-message';
import SessionOverviewIlmDuedate from '../session-overview-ilm-duedate';
import focus from '../../modifiers/focus';

export default class SessionIlmComponent extends Component {
  @service store;
  @tracked localHours;

  get hours() {
    if (this.localHours !== undefined) {
      return this.localHours;
    }

    return this.ilmSession?.hours;
  }

  get uniqueId() {
    return guidFor(this);
  }

  validations = new YupValidations(this, {
    hours: number().required().moreThan(0),
  });

  @cached
  get ilmSessionData() {
    return new TrackedAsyncData(this.args.session.ilmSession);
  }

  get ilmSession() {
    return this.ilmSessionData.isResolved ? this.ilmSessionData.value : null;
  }

  get isIndependentLearning() {
    return this.ilmSession !== null;
  }

  saveIndependentLearning = task({ drop: true }, async (value) => {
    if (!value) {
      const ilmSession = await this.args.session.ilmSession;
      this.args.session.set('ilmSession', null);
      await ilmSession.destroyRecord();
      await this.args.session.save();
    } else {
      const hours = 1;
      const dueDate = DateTime.now().plus({ week: 6 }).set({ hour: 17, minute: 0 }).toJSDate();
      this.localHours = hours;
      const ilmSession = this.store.createRecord('ilm-session', {
        session: this.args.session,
        hours,
        dueDate,
      });
      this.args.session.set('ilmSession', await ilmSession.save());
      await this.args.session.save();
    }
  });

  changeIlmHours = task({ restartable: true }, async () => {
    this.validations.addErrorDisplayFor('hours');
    const isValid = await this.validations.isValid();
    if (!isValid) {
      return false;
    }

    this.validations.removeErrorDisplayFor('hours');
    const ilmSession = await this.args.session.ilmSession;
    if (ilmSession) {
      ilmSession.hours = this.hours;
      await ilmSession.save();
      this.resetHours();
    }
  });

  resetHours = () => {
    this.validations.removeErrorDisplayFor('hours');
    this.localHours = undefined;
  };
  <template>
    <div class="session-ilm" data-test-session-ilm>
      <label>{{t "general.independentLearning"}}:</label>
      <span class="ilm-value" data-test-ilm-value>
        {{#if @editable}}
          <ToggleYesno
            @yes={{this.isIndependentLearning}}
            @toggle={{perform this.saveIndependentLearning}}
            data-test-ilm-toggle
          />
        {{else}}
          {{#if this.isIndependentLearning}}
            <span class="add">{{t "general.yes"}}</span>
          {{else}}
            <span class="remove">{{t "general.no"}}</span>
          {{/if}}
        {{/if}}
      </span>
    </div>
    {{#if this.isIndependentLearning}}
      <div class="overview-block hours" data-test-ilm-hours>
        <label for="hours-{{this.uniqueId}}">{{t "general.hours"}}:</label>
        <span>
          {{#if @editable}}
            <EditableField
              @value={{this.hours}}
              @save={{perform this.changeIlmHours}}
              @close={{this.resetHours}}
              as |keyboard isSaving|
            >
              <input
                id="hours-{{this.uniqueId}}"
                disabled={{isSaving}}
                type="text"
                value={{this.hours}}
                {{this.validations.attach "hours"}}
                {{on "input" (pick "target.value" (set this "localHours"))}}
                {{keyboard}}
                {{focus}}
              />
              <YupValidationMessage
                @description={{t "general.hours"}}
                @validationErrors={{this.validations.errors.hours}}
              />
            </EditableField>
          {{else}}
            {{@session.ilmSession.hours}}
          {{/if}}
        </span>
      </div>
      {{#unless @session.hasPostrequisite}}
        <SessionOverviewIlmDuedate
          @ilmSession={{this.ilmSession}}
          @editable={{@editable}}
          class="overview-block"
        />
      {{/unless}}
    {{/if}}
  </template>
}
